import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';

export const POLL_INTERVAL_MS =  5 * 6 * 10_000

const _channel = new BroadcastChannel('sc_integration_poll')

const INTEGRATION_CHANGE_EVENT = 'sc:integrationChange'

const onIntegrationResult = (handler: (result: any) => void) => {
  const listener = (e: MessageEvent) => handler(e.data)
  _channel.addEventListener('message', listener)
  return () => _channel.removeEventListener('message', listener)
}

// Fires in every tab whenever integration info is refreshed — whether this tab
// is the leader (direct dispatch) or a follower (via BroadcastChannel).
export const onIntegrationChange = (handler: () => void): (() => void) => {
  window.addEventListener(INTEGRATION_CHANGE_EVENT, handler)
  return () => window.removeEventListener(INTEGRATION_CHANGE_EVENT, handler)
}

// One tab becomes the leader and polls; others queue for the lock and only
// receive results via BroadcastChannel. The lock is held for the full loop so
// it is never released between polls — competitors stay idle until the leader
// tab closes.
export const startIntegrationPolling = (onResult: (result: any) => void): () => void => {
  const signal = { stopped: false }
  let wakeUp: (() => void) | null = null

  // Non-leader path: receive broadcasts from the leader.
  // Also dispatch the window event so onIntegrationChange fires in this tab.
  const unsubscribe = onIntegrationResult((result) => {
    onResult(result)
    window.dispatchEvent(new CustomEvent(INTEGRATION_CHANGE_EVENT))
  })

  // One immediate fetch so this tab isn't stale while waiting for the first
  // leader broadcast (relevant when a new tab opens mid-cycle).
  getAccountingIntegrationError()
    .then(result => { if (!signal.stopped) onResult(result) })
    .catch(() => {})

  // Compete to be the leader. No ifAvailable — tabs queue behind the current
  // leader and take over automatically when it closes.
  navigator.locks.request('sc_integration_poll', async () => {
    while (!signal.stopped) {
      const result = await getAccountingIntegrationError()
      if (!signal.stopped) {
        onResult(result)
        _channel.postMessage(result)
        // Leader path: BroadcastChannel doesn't self-deliver, so dispatch the
        // window event directly so onIntegrationChange fires in this tab too.
        window.dispatchEvent(new CustomEvent(INTEGRATION_CHANGE_EVENT))
      }
      await new Promise<void>(resolve => {
        const t = setTimeout(resolve, POLL_INTERVAL_MS)
        wakeUp = () => { clearTimeout(t); resolve() }
      })
      wakeUp = null
    }
  }).catch(() => {})

  return () => {
    signal.stopped = true
    wakeUp?.()
    unsubscribe()
  }
}

const getIntegration = async () => {

  let result: any = null;

  const integrations = await Fetch.get({
    url: '/Integration',
  });

  if (integrations.TotalResults > 0) {
    let integrationList = integrations.Results;
    result = integrationList.find((x: any) => x.Type == Enums.IntegrationType.Accounting && (x.IsActive || x.Status == Enums.IntegrationStatus.Unauthorized));
  }
  return result || null;
};

const canReceiveInventoryFromPartner = (integration?: any): boolean => {
  const direction = integration?.PartnerSettingsBaseDeserialized?.InventoryDirection;
  return direction === Enums.IntegrationDirection.From || direction === Enums.IntegrationDirection.Bidirectional;
};

const getAccountingIntegrationError = async () => {
  const result = await Fetch.get({ url: '/IntegrationInfo' });
  if (!result || result.Status == null) return null;

  if (result.Status === Enums.IntegrationStatus.Unauthorized) return result;
  if (result.IsLive && result.ErrorMessage) return result;
  return null;
};

const getExternalApiKey = async () => {
  const result = await Fetch.get({ url: '/Integration/ExternalApiKey' });
  if (result && result.ID) return result;
  return null;
};

const createExternalApiKey = async () => {
  return await Fetch.post({ url: '/Integration/ExternalApiKey' });
};

const updateExternalApiKey = async (id: string, isActive: boolean) => {
  return await Fetch.put({
    url: `/Integration/ExternalApiKey/${id}`,
    params: { IsActive: isActive },
  });
};

export default {
  getIntegration,
  canReceiveInventoryFromPartner,
  getAccountingIntegrationError,
  startIntegrationPolling,
  onIntegrationChange,
  getExternalApiKey,
  createExternalApiKey,
  updateExternalApiKey,
  POLL_INTERVAL_MS,
};
